import BookingDetails from '@components/BookingDetails/BookingDetails';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  ButtonsWrapper,
  ExternalWrapper,
  InnerContainer,
  LeftButtonsWrapper,
  RightContainer,
  StepContainer,
} from '@components/Steps/Steps.styles';
import {
  CheckIcon,
  CloseIcon,
  InformationIcon,
  LuxButton,
  LuxModal,
  LuxModalFooter,
  LuxModalHeader,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SubtitleWrapper,
  ThankYouMessageWrapper,
  TypWrapper,
} from '@components/ThankYouPage/ThankYouPage.styles';
import { CancelConfirmCard } from './CancelConfirmPage.styles';
import useGetAppointmentData from '@hooks/useGetAppointmentData';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { useHistory } from 'react-router-dom';
import useConfirmAppointment from '@hooks/useConfirmAppointment';
import { conditionallyApplyUpperCase, resetApp } from '@utils/functions';
import useDeleteAppointment from '@hooks/useDeleteAppointment';
import { Helmet } from 'react-helmet';
import AppointmentErrorModal from '@components/AppointmentErrorModal/AppointmentErrorModal';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useAnalytics, { AnalyticsActionType } from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { IntegrationModes } from '@utils/enums/IntegrationModes.enum';
import { BrandNameLabelList } from '@utils/consts/brandNameList';

interface ConfirmCancelErrorProps {
  response?: {
    data?: {
      code?: string;
    };
  };
}

const CancelConfirmPage = () => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const theme = useTheme();
  const { trackPageAnalytics, trackEventAnalytics } = useAnalytics();
  const history = useHistory();
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);
  const links = useOnlineBookingStore(state => state.links);
  // const btwMdAndLg = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const btwXsAndSm = useMediaQuery(theme.breakpoints.between('xs', 'sm'));

  const appointmentIdFromUrl = useMemo(() => {
    const queryString =
      integrationMode === IntegrationModes.WCS
        ? window.location.hash.split('?')[1] || ''
        : window.location.search.substring(1);

    return new URLSearchParams(queryString).get('appointmentId');
  }, []);

  const appointmentId =
    useOnlineBookingStore(state => state.appointmentId) || appointmentIdFromUrl;
  const [mode, setMode] = useState(() => {
    const queryString =
      integrationMode === IntegrationModes.WCS
        ? window.location.hash.split('?')[1] || ''
        : window.location.search.substring(1);

    return new URLSearchParams(queryString).get('mode');
  });

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [modalSize, setModalSize] = useState<'small' | 'medium'>('medium');
  const practiceId = useOnlineBookingStore(state => state.practiceId);

  const getPatients = usePatientStore(state => state.getPatients);
  const patients = getPatients();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const hideBackToBrandHomePage = useOnlineBookingStore(
    state => state.hideBackToBrandHomePage,
  );

  useEffect(() => {
    if (mode === 'canceled') {
      trackPageAnalytics({
        page: FlowsPages.CANCELED,
        patients,
      });
    }
  }, [mode]);

  const { data } = useGetAppointmentData({ appointmentId });

  const patientId = useMemo(() => {
    return data?.AppointmentExam?.patientId || undefined;
  }, [data]);

  const {
    mutate: confirmAppointment,
    isSuccess: isSuccessConf,
    isLoading: isLoadingConf,
    isError: isErrorConf,
    error: errorConfRaw,
  } = useConfirmAppointment({
    appointmentId,
    patientId,
  });
  const errorConf = errorConfRaw as ConfirmCancelErrorProps | undefined;
  const {
    mutate: deleteAppointment,
    isSuccess: isSuccessDel,
    isLoading: isLoadingDel,
    isError: isErrorDel,
    error: errorDelRaw,
  } = useDeleteAppointment({
    appointmentId,
    practiceId,
  });
  const errorDel = errorDelRaw as ConfirmCancelErrorProps | undefined;
  const updatePatient = usePatientStore(state => state.updatePatient);
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );
  const generalData = useOnlineBookingStore(state => state.generalConfig);

  const mediaQuery = window.matchMedia('(min-width: 576px)');

  useEffect(() => {
    if (!isLoadingConf && isSuccessConf) {
      setMode('confirmed');
    }
  }, [isLoadingConf, isSuccessConf]);

  useEffect(() => {
    if (!isLoadingDel && isSuccessDel) {
      setMode('canceled');
    }
  }, [isLoadingDel, isSuccessDel]);

  useEffect(() => {
    if (isErrorDel || isErrorConf) {
      setMode('error');
    }
  }, [isErrorDel, isErrorConf]);

  const handleSizeChange = (e: MediaQueryListEvent) => {
    if (e.matches) {
      setModalSize('medium');
    } else setModalSize('small');
  };
  mediaQuery.addEventListener('change', handleSizeChange);

  useEffect(() => {
    setCurrentPatient(0);
  }, [setCurrentPatient]);

  useEffect(() => {
    if (data) {
      console.log('##data', data);

      updatePatient(0, FormIds.AppointmentType, {
        appointmentType: {
          name:
            brandName && data?.idTranslation
              ? t(
                  `appointmentTypePage.${brandName}.${data.idTranslation}.title`,
                )
              : data.appointmentType,
          idTranslation: data?.idTranslation,
        },
        insurance: {
          type: data.insurance.type || undefined,
          id: data.insurance.id || '',
          name: data.insurance.name || undefined,
        },
        scheduleExam: {
          day: data.AppointmentExam.date,
          hour: data.AppointmentExam.startTime,
          doctor: data.AppointmentExam.doctorName,
        },
      });
    }
  }, [data, updatePatient]);

  const actionCode = useMemo(() => {
    return new URLSearchParams(window.location.search).get('mode') || undefined;
  }, [window.location.search]);

  const statusCode = useMemo(() => {
    return errorDel?.response?.data?.code || errorConf?.response?.data?.code;
  }, [errorDel, errorConf, t]);

  return (
    <>
      {mode && (
        <Helmet>
          <title>
            {mode === 'confirm'
              ? t('wcag.ob_manage_confirm_title')
              : mode === 'cancel'
                ? t('wcag.ob_manage_cancel_title')
                : null}
          </title>
        </Helmet>
      )}
      <ExternalWrapper
        backgroundColor={
          brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
        }
      >
        {mode === 'error' && (
          <AppointmentErrorModal
            statusCode={statusCode}
            actionCode={actionCode}
          />
        )}
        {mode !== 'error' && (
          <TypWrapper>
            {mode !== 'canceled' && (
              <ThankYouMessageWrapper>
                <Stack
                  style={{
                    width: '2.75rem',
                    height: '2.75rem',
                    borderRadius: '100%',
                    border: `1px solid ${mode === 'confirmed' ? '#23AE3133' : theme.palette.LUX.obTokens.bordersColorFunctionalInfo}`,
                    backgroundColor: `${mode === 'confirmed' ? '#E2FCE5' : theme.palette.LUX.obTokens.bgColorFunctionalInfo}`,
                  }}
                  justifyContent="center"
                  alignItems="center"
                >
                  {mode === 'confirmed' ? (
                    <CheckIcon color={theme.palette.LUX.content.primary} />
                  ) : (
                    <InformationIcon
                      color={theme.palette.LUX.content.primary}
                    />
                  )}
                </Stack>
                {mode === 'confirmed' ? (
                  <SubtitleWrapper>
                    <LuxTypography variant="heading3">
                      {t('thankYouMessages.appointmentBooked')}
                    </LuxTypography>
                    <LuxTypography variant="textMdRegular">
                      {t('thankYouMessages.cantWaitToSeeYou')}
                    </LuxTypography>
                  </SubtitleWrapper>
                ) : (
                  <LuxTypography variant="heading3">
                    {t('manageAppointment.manage')}
                  </LuxTypography>
                )}
              </ThankYouMessageWrapper>
            )}
            <InnerContainer
              canceledPage={mode === 'canceled' || mode === 'confirmed'}
            >
              {mode !== 'confirmed' && (
                <StepContainer
                  borderColor={
                    theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                  }
                >
                  {mode !== 'canceled' && mode !== 'confirmed' && (
                    <CardTitleWrapper
                      borderColor={
                        theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                      }
                    >
                      <LuxTypography variant="heading4">
                        {mode === 'confirm'
                          ? t('manageAppointment.confirmTitle')
                          : t('manageAppointment.cancelTitle')}
                      </LuxTypography>
                    </CardTitleWrapper>
                  )}
                  <CancelConfirmCard canceled={mode === 'canceled'}>
                    {mode === 'canceled' && (
                      <Stack
                        style={{
                          width: '2.75rem',
                          height: '2.75rem',
                          borderRadius: '100%',
                          backgroundColor:
                            theme.palette.LUX.obTokens.bgColorFunctionalError,
                          border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalError}`,
                        }}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CloseIcon color={theme.palette.LUX.content.primary} />
                      </Stack>
                    )}
                    <LuxTypography
                      variant={mode === 'cancel' ? 'heading4' : 'heading3'}
                      fontSize={mode === 'cancel' ? '1.125rem' : undefined}
                    >
                      {mode === 'canceled'
                        ? t('manageAppointment.canceledTitle')
                        : mode === 'cancel'
                          ? t('manageAppointment.cancel')
                          : t('manageAppointment.confirm')}
                    </LuxTypography>
                    <LuxTypography variant={'textMdRegular'}>
                      {mode === 'canceled'
                        ? t('manageAppointment.canceledSubtitle')
                        : mode === 'cancel'
                          ? t('manageAppointment.pleaseCancel')
                          : t('manageAppointment.pleaseConfirm')}
                    </LuxTypography>
                  </CancelConfirmCard>
                </StepContainer>
              )}
              {mode !== 'canceled' && (
                <RightContainer>
                  <BookingDetails
                    hideDownloadButton
                    hideEditButton
                    isFullRecap={mode === 'confirmed'}
                    manageOrRescheduleMode
                  />
                </RightContainer>
              )}
            </InnerContainer>
          </TypWrapper>
        )}
      </ExternalWrapper>
      {mode !== 'error' && (
        <ButtonsWrapper
          step={mode === 'canceled' ? 1 : 0}
          fromTyp={mode === 'canceled'}
          onlySecondary={!brandName || !links?.homePage}
        >
          {
            /*first buttons condition case */
            (((mode === 'canceled' || mode === 'confirmed') &&
              generalData?.customization?.secondaryButtonLabel &&
              generalData?.customization?.secondaryButtonLink) ||
              /* second buttons condition case */
              (brandName && links?.homePage && mode === 'canceled')) && (
              <LeftButtonsWrapper>
                {(mode === 'canceled' || mode === 'confirmed') &&
                  generalData?.customization?.secondaryButtonLabel &&
                  generalData?.customization?.secondaryButtonLink && (
                    <LuxButton
                      variant="secondary"
                      iconStart="HomeIcon"
                      onClick={() =>
                        window.open(
                          generalData?.customization?.secondaryButtonLink,
                          '_self',
                        )
                      }
                    >
                      <LuxTypography
                        variant={'textMdRegular'}
                        fontWeight={brandName && '700!important'}
                      >
                        {generalData?.customization?.secondaryButtonLabel ??
                          t('generalCta.toEcp')}
                      </LuxTypography>
                    </LuxButton>
                  )}
                {brandName &&
                  links?.homePage &&
                  !hideBackToBrandHomePage &&
                  mode === 'canceled' && (
                    <LuxButton
                      variant="secondary"
                      iconStart="LogoutIcon"
                      onClick={() =>
                        links.homePage &&
                        (window.location.href = links?.homePage)
                      }
                    >
                      <LuxTypography
                        variant={'textMdRegular'}
                        fontWeight={brandName && '700!important'}
                      >
                        {t('generalCta.backToBrandHomePage', {
                          brandName:
                            BrandNameLabelList[
                              brandName as keyof typeof BrandNameLabelList
                            ],
                        }).toUpperCase()}
                      </LuxTypography>
                    </LuxButton>
                  )}
              </LeftButtonsWrapper>
            )
          }
          {mode !== 'confirmed' && (
            <LuxButton
              variant="primary"
              onClick={() => {
                switch (mode) {
                  case 'canceled':
                    resetApp(true);
                    history.push(Urls.APPOINTMENT_TYPE);
                    break;
                  case 'confirm':
                    confirmAppointment();
                    break;
                  case 'cancel':
                  default:
                    setOpenCancelModal(true);
                    break;
                }
              }}
              style={{
                ...(!brandName && mode === 'cancel'
                  ? {
                      backgroundColor: `${theme.palette.LUX.obTokens?.bgColorFunctionalError}`,
                      color: '#001625',
                      border: `1px solid ${theme.palette.LUX.obTokens?.bordersColorFunctionalError}`,
                    }
                  : {}),
              }}
              iconStart={
                !brandName && mode === 'cancel'
                  ? 'CloseIcon'
                  : mode === 'canceled'
                    ? 'CalendarIcon'
                    : undefined
              }
            >
              <LuxTypography
                variant={'textMdRegular'}
                fontWeight={brandName && '700!important'}
              >
                {mode === 'cancel'
                  ? conditionallyApplyUpperCase(
                      t('manageAppointment.cancel'),
                      brandName,
                    )
                  : mode === 'canceled'
                    ? conditionallyApplyUpperCase(
                        t('manageAppointment.bookNew'),
                        brandName,
                      )
                    : conditionallyApplyUpperCase(
                        t('manageAppointment.confirm'),
                        brandName,
                      )}
              </LuxTypography>
            </LuxButton>
          )}
        </ButtonsWrapper>
      )}
      <LuxModal
        open={openCancelModal}
        size={modalSize}
        onClose={() => setOpenCancelModal(false)}
        hideFooterShadow
        dividerFooter
        sx={{
          '& .lux-modal-header': {
            padding: '2.5rem 1.75rem 0',
          },
          '& .lux-modal-content-wrapper': {
            padding: '0 1.5rem',
          },
        }}
        header={
          <LuxModalHeader
            darkMode={false}
            enableCloseIcon
            onClose={() => setOpenCancelModal(false)}
            title={
              <LuxTypography variant="heading3">
                {t('manageAppointment.cancel')}
              </LuxTypography>
            }
          />
        }
        footer={
          <LuxModalFooter
            buttonsFlexDirection={btwXsAndSm ? 'column' : 'row'}
            justifyContent="space-between"
            onSecondaryButtonClick={() => {
              setOpenCancelModal(false);
            }}
            onPrimaryButtonClick={() => {
              deleteAppointment();
              setOpenCancelModal(false);
              //analytics event track
              trackEventAnalytics({
                action: AnalyticsActionType.CANCEL_APPOINTMENT,
              });
            }}
            primaryButtonText={
              brandName
                ? `${t('manageAppointment.yes').toUpperCase()}`
                : `${t('manageAppointment.yes')}`
            }
            secondaryButtonText={
              brandName
                ? `${t('manageAppointment.no').toUpperCase()}`
                : `${t('manageAppointment.no')}`
            }
            primaryButtonSx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
            secondaryButtonSx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
          />
        }
      >
        <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
          {t('manageAppointment.sureCancel')}
        </LuxTypography>
      </LuxModal>
    </>
  );
};

export default CancelConfirmPage;
